import { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import PromotionUtils from 'modules/promotions/utils/promotion-utils';
import { usePromotions, usePromotionsLoadDone } from 'modules/promotions/hooks';
import { useSortedItems } from 'modules/common/hooks';
import { useLoadingStatusMetric } from 'modules/performance/hooks';
import { isEmpty, omit } from 'mojito/utils';

const classUtils = MojitoCore.Base.classUtils;
const { TARGET_TYPE } = MojitoServices.Promotions.types;
const { FlexPane, MenuRow, MenuRowSkeleton, LoaderSuspense, PostponedRenderer } =
    MojitoPresentation.Components;
const intentActions = MojitoCore.Intents.actions;

export default function MenuLinkPromotions(props) {
    const {
        applicablePage,
        extraLinks,
        onDataLoad,
        mojitoTools: { config, appContext, instanceId },
    } = props;
    const locationId = props.locationId || config.locationId;
    const { routeResolver } = appContext;

    const promotions = usePromotions(locationId, applicablePage);
    const menuPromotions = useSortedItems(promotions, config.priorityType.value);

    const menuItems = useMemo(() => {
        const toMenuItem = menuItemMapper(config, routeResolver, appContext);
        const promotionLinks = menuPromotions.map(toMenuItem);
        const allLinks = config.extraLinksInFront
            ? [extraLinks, promotionLinks]
            : [promotionLinks, extraLinks];

        return allLinks.flat().filter(item => item?.onClickIntent && item?.data);
    }, [config, routeResolver, menuPromotions, extraLinks, appContext]);

    const promotionsLoadDone = usePromotionsLoadDone();

    const performanceElementRef = useLoadingStatusMetric(
        promotionsLoadDone,
        config.reportLoadingStatus ? instanceId : undefined,
        'MenuLinkPromotions'
    );

    useEffect(() => {
        promotionsLoadDone && onDataLoad(menuItems);
    }, [promotionsLoadDone, menuItems, onDataLoad]);

    const noContent = promotionsLoadDone && isEmpty(menuItems);
    if (noContent) {
        return null;
    }

    return (
        <LinksContent
            {...props}
            menuItems={menuItems}
            performanceElementRef={performanceElementRef}
            loadDone={promotionsLoadDone}
        />
    );
}

// eslint-disable-next-line react/display-name
const LinksContent = memo(props => {
    const {
        mojitoTools: { config },
        loadDone,
        menuItems,
        onLinkClick,
        performanceElementRef,
        class: className,
    } = props;
    const dispatch = useDispatch();
    const onItemClick = (id, item) => {
        const { onClickIntent: intent } = item;
        intent && dispatch(intentActions.publishIntent(intent.type, intent.data));
        onLinkClick(item);
    };

    const skeleton = (
        <MenuRowSkeleton config={config.menuRowSkeleton} elementRef={performanceElementRef} />
    );

    const initialDataRef = useRef(loadDone);

    return (
        <FlexPane
            config={config.container}
            class={classUtils.classNames('ta-MenuLinkPromotions', className)}
        >
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!loadDone}
                loader={skeleton}
            >
                {!isEmpty(menuItems) && (
                    <MenuRowContent
                        config={config}
                        hasInitialData={initialDataRef.current}
                        menuItems={menuItems}
                        cbItemClicked={onItemClick}
                        elementRef={performanceElementRef}
                    />
                )}
            </LoaderSuspense>
        </FlexPane>
    );
});

const MenuRowContent = props => {
    const { postponedContentOptimization } = props.config;
    const disablePostponedOptimization = props.hasInitialData || !postponedContentOptimization;
    return (
        <PostponedRenderer name={'MenuRowContent'} disabled={disablePostponedOptimization}>
            <MenuRow {...omit(props, 'hasInitialData')} config={props.config.menuRow} />
        </PostponedRenderer>
    );
};

// ////////////////////////////////////////////////
// ///////// Internal helper functions

const getSportIcon = (sportId, sportIcons) => sportIcons[sportId] || sportIcons.defaultIcon;

const menuItemMapper = (config, routeResolver, appContext) => promotion => {
    const { id, imgUrl, linkText, target = {}, altText } = promotion;
    const { sportId, classId, eventId, typeId, meetingId, type: targetType } = target;
    const onClickIntent = PromotionUtils.resolveIntentFromPromotion(promotion);
    const linkPath = onClickIntent && onClickIntent.data;
    const icon =
        targetType === TARGET_TYPE.SPORT ? getSportIcon(sportId, config.sportIcons) : imgUrl;
    const hrefLink =
        targetType === TARGET_TYPE.EXTERNAL_URL
            ? target.url
            : linkPath && routeResolver.getRoute(linkPath);
    return {
        id,
        icon,
        data: linkText || id,
        hrefLink,
        onClickIntent,
        className: sportId || classId || typeId || meetingId || eventId || TARGET_TYPE.EXTERNAL_URL,
        altText: typeof altText !== 'undefined' ? altText : appContext.sportName(sportId),
    };
};
